import * as React from "react";
import { loader } from "graphql.macro";
import { useClient, CombinedError } from "urql";
import { useIsMounted } from "../useIsMounted";
import {
  XpTransactionsQueryQuery,
  XpTransactionsQueryQueryVariables,
} from "./graphql/XPTransactionsQuery.generated";

type XPTransactionsType = NonNullable<
  XpTransactionsQueryQuery["me"]
>["xpTransactions"];

const XP_TRANSACTIONS_QUERY = loader("./graphql/XPTransactionsQuery.graphql");

const mergeXPPages = (
  prev: XPTransactionsType,
  current: XPTransactionsType
) => {
  const existingIds = new Set<string>(prev.edges.map(({ node }) => node.id));
  for (const edge of current.edges)
    if (existingIds.has(edge.node.id)) return prev;

  return {
    edges: [...prev.edges, ...current.edges],
    pageInfo: current.pageInfo,
    __typename: current.__typename,
  };
};

/*Unfortunately we cannot use relay pagination for these.Due to network refresh
Check out https://github.com/FormidableLabs/urql/issues/672*/
export const useXPTransactions = () => {
  const client = useClient();
  const [error, setError] = React.useState<CombinedError | undefined>(
    undefined
  );
  const [XPTransactions, setXPTransactions] = React.useState<
    XPTransactionsType | undefined
  >();

  const isMounted = useIsMounted();

  const fetchXPMore = React.useCallback(
    (cursor: string) => {
      return client
        .query<XpTransactionsQueryQuery, XpTransactionsQueryQueryVariables>(
          XP_TRANSACTIONS_QUERY,
          { after: cursor },
          { requestPolicy: "network-only" }
        )
        .toPromise()
        .then(({ data, error }) => {
          if (error !== undefined) {
            throw error;
          } else if ((data?.me ?? null) === null) {
            setXPTransactions(undefined);
          } else if (isMounted.current) {
            const XPTransactions = data!.me!.xpTransactions;
            setXPTransactions((prev) => {
              if (prev === undefined) return XPTransactions;
              return mergeXPPages(prev, XPTransactions);
            });
          }
        })
        .catch((error) => {
          if (isMounted.current) setError(error);
        });
    },
    [client, isMounted]
  );

  React.useEffect(() => {
    fetchXPMore("");
  }, [fetchXPMore]);

  if (error !== undefined) throw error;

  return React.useMemo(
    () => ({
      XPTransactions,
      fetchXPMore,
    }),
    [XPTransactions, fetchXPMore]
  );
};
